import { useInfiniteQuery } from '@tanstack/react-query';

import type { SortDirection } from '@cast/design-system';
import type { ClmIncompatibleWorkloadsListResponse } from '@cast/types';
import { QueryOptions } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';
import { makeIsEnabled } from '@cast/utils';
import { useCurrentOrganization } from '@cast/infrastructure-organization';

const CLM_INCOMPATIBLE_WORKLOADS_QUERY_KEY = 'clm-incompatible-workloads';

const getClmIncompatibleWorkloadsQueryKey = ({
  organizationId,
  clusterId,
  sortField,
  sortOrder,
  pageLimit,
}: {
  organizationId: string;
  clusterId?: string;
  sortField?: string;
  sortOrder?: SortDirection;
  pageLimit: number;
}) =>
  [
    CLM_INCOMPATIBLE_WORKLOADS_QUERY_KEY,
    organizationId,
    clusterId,
    sortField,
    sortOrder,
    pageLimit,
  ] as const;

type Params = {
  clusterId: string;
  sortField?: string;
  sortOrder?: SortDirection;
  pageLimit?: number;
};

type UseClmIncompatibleWorkloadsQueryArgs = {
  params: Params;
  options?: QueryOptions<ClmIncompatibleWorkloadsListResponse>;
};

const DEFAULT_PAGE_LIMIT = 50;

export const useClmIncompatibleWorkloadsQuery = ({
  params,
  options,
}: UseClmIncompatibleWorkloadsQueryArgs) => {
  const currentOrganization = useCurrentOrganization();
  const organizationId = currentOrganization.id;
  const {
    clusterId,
    sortField,
    sortOrder,
    pageLimit = DEFAULT_PAGE_LIMIT,
  } = params;

  return useInfiniteQuery({
    queryKey: getClmIncompatibleWorkloadsQueryKey({
      organizationId,
      clusterId,
      sortField,
      sortOrder,
      pageLimit,
    }),
    queryFn: async ({ pageParam }) => {
      const { data: responseData } =
        await apiClient.costReport.listContainerLiveMigrationIncompatibleWorkloads(
          {
            organizationId,
            clusterId,
            pageLimit: String(pageLimit),
            pageCursor: pageParam,
            sortField,
            sortOrder,
          }
        );

      return responseData;
    },
    enabled: makeIsEnabled(!!clusterId, options?.enabled),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextPageCursor || undefined,
    select: (data) => {
      return {
        items: data.pages.flatMap((page) => page.items ?? []),
        totalCount: data.pages[0]?.totalCount ?? 0,
      };
    },
  });
};
